import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Building2,
  Search,
  MapPin,
  Users,
  Briefcase,
  Heart,
  ExternalLink
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { toast } from 'sonner';

export function FollowingSection() {
  const { companies, followedCompanies, handleFollowCompany } = useApp();
  const [searchTerm, setSearchTerm] = useState('');

  // Get followed companies
  const followedCompaniesData = companies?.filter(c =>
    followedCompanies?.includes(c.id)
  ) || [];

  // Get all companies for discovery
  const allCompanies = companies || [];

  // Filter companies
  const filteredCompanies = searchTerm
    ? allCompanies.filter(c =>
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.industry?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : allCompanies;

  const isFollowing = (companyId: string) => {
    return followedCompanies?.includes(companyId);
  };

  const handleToggleFollow = (companyId: string) => {
    try {
      handleFollowCompany(companyId);
      toast.success(
        isFollowing(companyId) ? 'Unfollowed company' : 'Following company'
      );
    } catch (error) {
      toast.error('Failed to update follow status');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Following</h2>
          <p className="text-gray-600 mt-1">Companies you follow and their latest jobs</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {followedCompaniesData.length} Companies
        </Badge>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              placeholder="Search companies..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Following Companies */}
      {followedCompaniesData.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">Companies You Follow</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {followedCompaniesData.map((company) => (
              <Card key={company.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  {/* Company Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-gray-400" />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFollow(company.id)}
                    >
                      <Heart className="h-4 w-4 fill-red-500 text-red-500" />
                    </Button>
                  </div>

                  {/* Company Info */}
                  <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-1">
                    {company.name}
                  </h3>
                  <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                    {company.description || 'No description available'}
                  </p>

                  {/* Company Stats */}
                  <div className="space-y-1.5 mb-3 text-xs text-gray-500">
                    {company.location && (
                      <div className="flex items-center gap-1.5">
                        <MapPin className="h-3.5 w-3.5" />
                        <span className="line-clamp-1">{company.location}</span>
                      </div>
                    )}
                    {company.industry && (
                      <div className="flex items-center gap-1.5">
                        <Briefcase className="h-3.5 w-3.5" />
                        <span className="line-clamp-1">{company.industry}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" size="sm">
                      View Jobs
                    </Button>
                    <Button variant="outline" size="sm">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Discover Companies */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">
          {searchTerm ? 'Search Results' : 'Discover Companies'}
        </h3>

        {filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {searchTerm ? 'No companies found' : 'No companies available'}
              </h3>
              <p className="text-gray-500">
                {searchTerm ? 'Try adjusting your search' : 'Check back later for companies'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredCompanies
              .filter(c => !isFollowing(c.id))
              .slice(0, 8)
              .map((company) => (
                <Card key={company.id} className="hover:shadow-lg transition-all">
                  <CardContent className="p-4">
                    {/* Company Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-400" />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleFollow(company.id)}
                      >
                        <Heart className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>

                    {/* Company Info */}
                    <h3 className="font-semibold text-base text-gray-900 mb-1 line-clamp-1">
                      {company.name}
                    </h3>
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                      {company.description || 'No description available'}
                    </p>

                    {/* Company Stats */}
                    <div className="space-y-1.5 mb-3 text-xs text-gray-500">
                      {company.location && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          <span className="line-clamp-1">{company.location}</span>
                        </div>
                      )}
                      {company.industry && (
                        <div className="flex items-center gap-1.5">
                          <Briefcase className="h-3.5 w-3.5" />
                          <span className="line-clamp-1">{company.industry}</span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        size="sm"
                        onClick={() => handleToggleFollow(company.id)}
                      >
                        Follow
                      </Button>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
